import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
    Paper,
    Grid,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Typography,
    Box,
    MenuItem,
    Divider,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    IconButton,
} from "@mui/material";
import {
    Save,
    ExpandMore,
    Dns,
    VerifiedUser,
    DeleteOutline,
    Add,
    Close,
    WarningAmber,
} from "@mui/icons-material";
import useNotification from "../../common/Notification";
import { WB_SX } from "../../../ui/ux/uiTokens";
import HealthBadge from "../shared/HealthBadge";
import SubscriptionFields from "../shared/SubscriptionFields";
import SesDnsRecordsTable from "../shared/SesDnsRecordsTable";
import {
    getCredits,
    updateCredits,
    getEmailConfig,
    createEmailConfig,
    updateEmailConfig,
    deleteEmailConfig,
    getUserSubscriptions,
    updateSubscription,
    getUserServiceStatus,
    updateUserRoles,
    listAwsSesAccounts,
    updateUserProfile,
} from "../../../api/superAdminApi";
import {
    listEmailDomains,
    createEmailDomain,
    getEmailDomainDnsRecords,
    verifyEmailDomain,
    deleteEmailDomain,
    listSenderEmails,
    createSenderEmail,
    deleteSenderEmail,
    isDomainVerified,
} from "../../../api/emailBusinessApi";
import { parseUserRoles } from "../../UserManagement/roleLabels";

const SES_STATUS_COLOR = {
    SUCCESS: "success",
    PENDING: "warning",
    FAILED: "error",
    TEMPORARY_FAILURE: "error",
};

const domainStatusLabel = (domain) => {
    const status = String(domain?.ses_verification_status ?? domain?.status ?? "").toUpperCase();
    if (status === "SUCCESS" || status === "AUTHENTICATED") return "Verified";
    if (status === "PENDING") return "Pending";
    return "Not verified";
};

const EMPTY_WORKER_FORM = {
    name: "",
    username: "",
    reply_to: "",
    status: "INACTIVE",
    is_valid: 0,
    unique_email_count: "",
    ad_unique_email_count: "",
    smtp_username: "",
    password: "",
    smtp_region: "",
    host: "",
    port: 587,
    smtpsecure: "tls",
};

const AdminEmailServicePage = () => {
    const { user, reloadUser } = useOutletContext();
    const notify = useNotification();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [infoMessage, setInfoMessage] = useState("");

    const [emailConfig, setEmailConfig] = useState(null);
    const [workerForm, setWorkerForm] = useState(EMPTY_WORKER_FORM);
    const [emailCredits, setEmailCredits] = useState({ bec: "", dbec: "", adbec: "" });
    const [subscriptions, setSubscriptions] = useState(null);
    const [serviceStatus, setServiceStatus] = useState(null);

    const [sesAccounts, setSesAccounts] = useState([]);
    const [domains, setDomains] = useState([]);
    const [senders, setSenders] = useState([]);
    const [domainForm, setDomainForm] = useState({ domain: "", description: "" });
    const [assignedSesAccountId, setAssignedSesAccountId] = useState("");
    const [domainDialogOpen, setDomainDialogOpen] = useState(false);
    const [senderForm, setSenderForm] = useState({ email_domain_id: "", local_part: "", reply_to: "", name: "" });
    const [senderDialogOpen, setSenderDialogOpen] = useState(false);
    const [dnsDialog, setDnsDialog] = useState({ open: false, domain: null, records: [], loading: false });
    const [deleteWorkerOpen, setDeleteWorkerOpen] = useState(false);
    const [verifyingDomainId, setVerifyingDomainId] = useState(null);

    const verifiedDomains = useMemo(
        () => domains.filter(isDomainVerified),
        [domains]
    );

    const applyWorkerForm = useCallback((cfg) => {
        setEmailConfig(cfg);
        if (cfg) {
            setWorkerForm({
                name: cfg.name || "",
                username: cfg.username || "",
                reply_to: cfg.reply_to || "",
                status: cfg.status || "INACTIVE",
                is_valid: cfg.is_valid ? 1 : 0,
                unique_email_count: cfg.unique_email_count != null ? String(cfg.unique_email_count) : "",
                ad_unique_email_count: cfg.ad_unique_email_count != null ? String(cfg.ad_unique_email_count) : "",
                smtp_username: cfg.smtp_username || "",
                password: "",
                smtp_region: cfg.smtp_region || "",
                host: cfg.host || "",
                port: cfg.port ?? 587,
                smtpsecure: cfg.smtpsecure || "tls",
            });
        }
    }, []);

    const loadDomainsAndSenders = useCallback(async (userId) => {
        const [domainRows, senderRows] = await Promise.all([
            listEmailDomains({ user_id: userId, per_page: 100 }),
            listSenderEmails({ user_id: userId, per_page: 100 }),
        ]);
        setDomains(domainRows);
        setSenders(senderRows);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const [creditData, cfg, subs, status, accountsRes] = await Promise.all([
                getCredits(user.id),
                getEmailConfig(user.id),
                getUserSubscriptions(user.id).catch(() => null),
                getUserServiceStatus(user.id).catch(() => null),
                listAwsSesAccounts({ status: "ACTIVE", per_page: 100 }).catch(() => ({ accounts: [] })),
            ]);
            const em = creditData?.email || {};
            setEmailCredits({
                bec: em.bec != null ? String(em.bec) : "",
                dbec: em.dbec != null ? String(em.dbec) : "",
                adbec: em.adbec != null ? String(em.adbec) : "",
            });
            applyWorkerForm(cfg);
            setSubscriptions(subs);
            setServiceStatus(status);
            setSesAccounts(accountsRes.accounts || []);
            setAssignedSesAccountId(
                user.aws_ses_account_id != null ? String(user.aws_ses_account_id) : ""
            );
            await loadDomainsAndSenders(user.id);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user.id, user.aws_ses_account_id, applyWorkerForm, loadDomainsAndSenders]);

    useEffect(() => {
        load();
    }, [load]);

    const ensureEmailRole = async () => {
        const roles = parseUserRoles(user.user_role);
        const next = new Set(roles);
        next.add("EMAIL");
        if (roles.length !== next.size || !roles.includes("EMAIL")) {
            await updateUserRoles(user.id, [...next], "replace");
        }
    };

    const handleSaveCredits = async () => {
        setSaving(true);
        setError("");
        try {
            const creditPayload = { service: "email" };
            let hasCredit = false;
            ["bec", "dbec", "adbec"].forEach((field) => {
                if (emailCredits[field] !== "") {
                    creditPayload[field] = parseInt(emailCredits[field], 10);
                    hasCredit = true;
                }
            });
            if (!hasCredit) {
                setError("Enter at least one credit value to update.");
                return;
            }
            await updateCredits(user.id, creditPayload);
            notify("Email credits saved", "success");
            reloadUser();
        } catch (err) {
            setError(err.message);
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const buildWorkerPayload = (form, { includePassword = true } = {}) => {
        const payload = {
            name: form.name || undefined,
            username: form.username || undefined,
            reply_to: form.reply_to || undefined,
            status: form.status,
            is_valid: form.is_valid ? 1 : 0,
            smtp_username: form.smtp_username || undefined,
            smtp_region: form.smtp_region || undefined,
            host: form.host || undefined,
            port: form.port ? Number(form.port) : undefined,
            smtpsecure: form.smtpsecure || undefined,
        };
        if (form.unique_email_count !== "") payload.unique_email_count = parseInt(form.unique_email_count, 10);
        if (form.ad_unique_email_count !== "") payload.ad_unique_email_count = parseInt(form.ad_unique_email_count, 10);
        if (includePassword && form.password) payload.password = form.password;
        return payload;
    };

    const handleCreateWorker = async () => {
        setSaving(true);
        setError("");
        try {
            const result = await createEmailConfig({
                user_id: user.id,
                ...buildWorkerPayload(workerForm),
            });
            await ensureEmailRole();
            applyWorkerForm(result.data);
            notify("Email worker created", "success");
            reloadUser();
        } catch (err) {
            setError(err.message);
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateWorker = async () => {
        if (workerForm.status === "ACTIVE" && !workerForm.is_valid) {
            setError("Set worker as valid (is_valid = 1) before activating.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            await updateEmailConfig(user.id, buildWorkerPayload(workerForm));
            applyWorkerForm(await getEmailConfig(user.id));
            notify("Email worker updated", "success");
            reloadUser();
        } catch (err) {
            setError(err.message);
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteWorker = async () => {
        setSaving(true);
        try {
            const result = await deleteEmailConfig(user.id);
            setDeleteWorkerOpen(false);
            setEmailConfig(null);
            setWorkerForm(EMPTY_WORKER_FORM);
            notify(result.message || "Email worker deleted", "success");
            reloadUser();
        } catch (err) {
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSesAssignment = async () => {
        setSaving(true);
        setError("");
        try {
            await updateUserProfile(user.id, {
                aws_ses_account_id: assignedSesAccountId ? Number(assignedSesAccountId) : null,
            });
            notify("AWS SES account assignment saved", "success");
            reloadUser();
        } catch (err) {
            setError(err.message);
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleCreateDomain = async () => {
        const domain = domainForm.domain.trim().toLowerCase();
        if (!domain) {
            setError("Domain is required.");
            return;
        }
        if (!user.aws_ses_account_id) {
            setError("Assign an active AWS SES account to this user before creating domains.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const result = await createEmailDomain({
                domain,
                description: domainForm.description.trim() || undefined,
                user_id: user.id,
            });
            setDomainDialogOpen(false);
            setDomainForm({ domain: "", description: "" });
            setInfoMessage(result.ses?.message || result.message || "Domain created. Add DNS records and verify.");
            notify("Domain created", "success");
            await loadDomainsAndSenders(user.id);
            const created = result.data || result;
            if (result.ses?.dns_records?.length) {
                setDnsDialog({ open: true, domain: created, records: result.ses.dns_records, loading: false });
            }
        } catch (err) {
            setError(err.message);
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const openDomainDns = async (domain) => {
        setDnsDialog({ open: true, domain, records: [], loading: true });
        try {
            const data = await getEmailDomainDnsRecords(domain.id);
            setDnsDialog((prev) => ({
                ...prev,
                records: data.dns_records || [],
                loading: false,
            }));
        } catch (err) {
            notify(err.message, "error");
            setDnsDialog((prev) => ({ ...prev, loading: false }));
        }
    };

    const handleVerifyDomain = async (domainId) => {
        setVerifyingDomainId(domainId);
        try {
            const result = await verifyEmailDomain(domainId);
            setInfoMessage(result.ses?.message || result.message || "Verification checked");
            notify(
                result.ses?.is_verified ? "Domain verified" : "Domain not verified yet",
                result.ses?.is_verified ? "success" : "warning"
            );
            await loadDomainsAndSenders(user.id);
        } catch (err) {
            notify(err.message, "error");
        } finally {
            setVerifyingDomainId(null);
        }
    };

    const handleDeleteDomain = async (domainId) => {
        if (!window.confirm("Delete this domain and its SES identity from AWS?")) return;
        try {
            await deleteEmailDomain(domainId);
            notify("Domain deleted", "success");
            await loadDomainsAndSenders(user.id);
        } catch (err) {
            notify(err.message, "error");
        }
    };

    const handleCreateSender = async () => {
        const domain = verifiedDomains.find((d) => String(d.id) === String(senderForm.email_domain_id));
        if (!domain) {
            setError("Select a verified domain.");
            return;
        }
        const local = senderForm.local_part.trim().toLowerCase().replace(/@/g, "");
        const senderEmailId = `${local}@${domain.domain}`;
        if (!local) {
            setError("Sender local part is required.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            await createSenderEmail({
                email_domain_id: domain.id,
                sender_email_id: senderEmailId,
                reply_to: senderForm.reply_to.trim() || undefined,
                name: senderForm.name.trim() || undefined,
                user_id: user.id,
            });
            setSenderDialogOpen(false);
            setSenderForm({ email_domain_id: "", local_part: "", reply_to: "", name: "" });
            notify("Sender email created", "success");
            await loadDomainsAndSenders(user.id);
        } catch (err) {
            setError(err.message);
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteSender = async (id) => {
        if (!window.confirm("Delete this sender email?")) return;
        try {
            await deleteSenderEmail(id);
            notify("Sender deleted", "success");
            await loadDomainsAndSenders(user.id);
        } catch (err) {
            notify(err.message, "error");
        }
    };

    const handleSubscriptionSave = async (service, payload) => {
        setSaving(true);
        try {
            await updateSubscription(user.id, service, payload);
            notify("Subscription updated", "success");
            setSubscriptions(await getUserSubscriptions(user.id));
        } catch (err) {
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>;
    }

    return (
        <Paper elevation={0} sx={{ ...WB_SX.card, p: 3 }}>
            {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert> : null}
            {infoMessage ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfoMessage("")}>{infoMessage}</Alert> : null}

            {serviceStatus?.email && (
                <Box sx={{ mb: 2 }}><HealthBadge healthy={serviceStatus.email.is_healthy} /></Box>
            )}

            <Alert severity="info" sx={{ mb: 3, lineHeight: 1.65 }}>
                Recommended flow: (1) assign AWS SES account to user, (2) create email worker, (3) add and verify domain, (4) create sender under verified domain, (5) activate worker with <strong>status: ACTIVE</strong> and <strong>is_valid: 1</strong>.
            </Alert>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>AWS SES account assignment</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
                Domains use the AWS SES account assigned on the user profile. The domain API does not accept an account id from the client.
            </Typography>
            {!sesAccounts.length && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Create at least one ACTIVE AWS SES account before assigning users.
                </Alert>
            )}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel>Assigned AWS SES account</InputLabel>
                    <Select
                        label="Assigned AWS SES account"
                        value={assignedSesAccountId}
                        onChange={(e) => setAssignedSesAccountId(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {sesAccounts.map((a) => (
                            <MenuItem key={a.id} value={String(a.id)}>
                                {a.name} ({a.aws_region})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="outlined" size="small" disabled={saving || !sesAccounts.length} onClick={handleSaveSesAssignment}>
                    Save assignment
                </Button>
            </Box>
            {!user.aws_ses_account_id && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    This user has no AWS SES account assigned. Domain creation will fail until you assign one.
                </Alert>
            )}

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Email credits</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {["bec", "dbec", "adbec"].map((field) => (
                    <Grid item xs={12} sm={4} key={field}>
                        <TextField fullWidth size="small" label={field.toUpperCase()} type="number" value={emailCredits[field]} onChange={(e) => setEmailCredits((p) => ({ ...p, [field]: e.target.value }))} inputProps={{ min: 0 }} />
                    </Grid>
                ))}
            </Grid>
            <Button variant="outlined" size="small" disabled={saving} onClick={handleSaveCredits} sx={{ mb: 3 }}>
                Save credits
            </Button>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Email worker configuration</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
                Worker row controls sending script status and SMTP fallback. SES domain verification is managed separately below.
            </Typography>

            {!emailConfig ? (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[["Display name", "name"], ["From address (fallback)", "username"], ["Reply-to", "reply_to"]].map(([label, name]) => (
                        <Grid item xs={12} sm={6} key={name}>
                            <TextField fullWidth size="small" label={label} value={workerForm[name]} onChange={(e) => setWorkerForm((f) => ({ ...f, [name]: e.target.value }))} />
                        </Grid>
                    ))}
                    <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" label="Unique email count" type="number" value={workerForm.unique_email_count} onChange={(e) => setWorkerForm((f) => ({ ...f, unique_email_count: e.target.value }))} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" label="Allocated unique emails" type="number" value={workerForm.ad_unique_email_count} onChange={(e) => setWorkerForm((f) => ({ ...f, ad_unique_email_count: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} onClick={handleCreateWorker} disabled={saving} sx={WB_SX.primaryButton}>
                            Create email worker
                        </Button>
                    </Grid>
                </Grid>
            ) : (
                <>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                        <Chip size="small" label={emailConfig.is_valid ? "Valid" : "Not valid"} color={emailConfig.is_valid ? "success" : "warning"} variant="outlined" />
                        <Chip size="small" label={emailConfig.status} color={emailConfig.status === "ACTIVE" ? "success" : "default"} variant="outlined" />
                    </Box>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {[["Display name", "name"], ["From address (fallback)", "username"], ["Reply-to", "reply_to"]].map(([label, name]) => (
                            <Grid item xs={12} sm={6} key={name}>
                                <TextField fullWidth size="small" label={label} value={workerForm[name]} onChange={(e) => setWorkerForm((f) => ({ ...f, [name]: e.target.value }))} />
                            </Grid>
                        ))}
                        <Grid item xs={6} sm={3}>
                            <TextField fullWidth size="small" label="Unique email count" type="number" value={workerForm.unique_email_count} onChange={(e) => setWorkerForm((f) => ({ ...f, unique_email_count: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField fullWidth size="small" label="Allocated unique emails" type="number" value={workerForm.ad_unique_email_count} onChange={(e) => setWorkerForm((f) => ({ ...f, ad_unique_email_count: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField select fullWidth size="small" label="Worker status" value={workerForm.status} onChange={(e) => setWorkerForm((f) => ({ ...f, status: e.target.value }))}>
                                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField select fullWidth size="small" label="Is valid" value={workerForm.is_valid} onChange={(e) => setWorkerForm((f) => ({ ...f, is_valid: Number(e.target.value) }))}>
                                <MenuItem value={1}>Yes (1)</MenuItem>
                                <MenuItem value={0}>No (0)</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                    <Accordion disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}><Typography fontWeight={600}>SMTP fallback override</Typography></AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {[["SMTP username", "smtp_username"], ["SMTP password", "password", "password"], ["SMTP region", "smtp_region"], ["SMTP host", "host"]].map(([label, name, type = "text"]) => (
                                    <Grid item xs={12} sm={6} key={name}>
                                        <TextField fullWidth size="small" label={label} type={type} value={workerForm[name]} onChange={(e) => setWorkerForm((f) => ({ ...f, [name]: e.target.value }))} />
                                    </Grid>
                                ))}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                        <Button variant="contained" startIcon={<Save />} onClick={handleUpdateWorker} disabled={saving}>Save worker</Button>
                        <Button variant="outlined" color="error" startIcon={<DeleteOutline />} onClick={() => setDeleteWorkerOpen(true)} disabled={saving}>Delete worker</Button>
                    </Box>
                </>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Email domains</Typography>
                    <Typography variant="body2" color="text.secondary">SES identity verification per domain.</Typography>
                </Box>
                <Button variant="outlined" startIcon={<Add />} onClick={() => setDomainDialogOpen(true)} disabled={!user.aws_ses_account_id}>
                    Add domain
                </Button>
            </Box>
            {!user.aws_ses_account_id && (
                <Alert severity="warning" sx={{ mb: 2 }}>Assign an AWS SES account to this user before adding domains.</Alert>
            )}
            <TableContainer component={Paper} elevation={0} sx={{ ...WB_SX.card, mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {["Domain", "Verification status", "Actions"].map((h) => (
                                <TableCell key={h} sx={WB_SX.tableHeadCell}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {domains.length === 0 ? (
                            <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3 }}><Typography variant="body2" color="text.secondary">No domains</Typography></TableCell></TableRow>
                        ) : domains.map((d) => (
                            <TableRow key={d.id}>
                                <TableCell>{d.domain}</TableCell>
                                <TableCell>
                                    <Chip size="small" label={domainStatusLabel(d)} color={SES_STATUS_COLOR[d.ses_verification_status] || "default"} variant="outlined" />
                                </TableCell>
                                <TableCell>
                                    <Button size="small" startIcon={<Dns />} onClick={() => openDomainDns(d)} sx={{ mr: 0.5 }}>DNS</Button>
                                    <Button size="small" startIcon={verifyingDomainId === d.id ? <CircularProgress size={14} /> : <VerifiedUser />} onClick={() => handleVerifyDomain(d.id)} disabled={verifyingDomainId === d.id || isDomainVerified(d)} sx={{ mr: 0.5 }}>
                                        Verify
                                    </Button>
                                    <Button size="small" color="error" startIcon={<DeleteOutline />} onClick={() => handleDeleteDomain(d.id)}>Delete</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Sender emails</Typography>
                    <Typography variant="body2" color="text.secondary">Requires a verified domain.</Typography>
                </Box>
                <Button variant="outlined" startIcon={<Add />} onClick={() => setSenderDialogOpen(true)} disabled={!verifiedDomains.length}>
                    Add sender
                </Button>
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ ...WB_SX.card, mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {["Sender", "Reply-to", ""].map((h) => (
                                <TableCell key={h || "a"} sx={WB_SX.tableHeadCell}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {senders.length === 0 ? (
                            <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3 }}><Typography variant="body2" color="text.secondary">No senders</Typography></TableCell></TableRow>
                        ) : senders.map((s) => (
                            <TableRow key={s.id}>
                                <TableCell>{s.sender_email_id || s.email}</TableCell>
                                <TableCell>{s.reply_to || "—"}</TableCell>
                                <TableCell align="right">
                                    <Button size="small" color="error" startIcon={<DeleteOutline />} onClick={() => handleDeleteSender(s.id)}>Delete</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <SubscriptionFields service="email" sub={subscriptions?.email} onSave={handleSubscriptionSave} saving={saving} />

            <Dialog open={domainDialogOpen} onClose={() => !saving && setDomainDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
                    Add domain
                    <IconButton onClick={() => setDomainDialogOpen(false)}><Close /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Domain" required value={domainForm.domain} onChange={(e) => setDomainForm((f) => ({ ...f, domain: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Description" multiline minRows={2} value={domainForm.description} onChange={(e) => setDomainForm((f) => ({ ...f, description: e.target.value }))} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDomainDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateDomain} disabled={saving || !domainForm.domain.trim()}>Create domain</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={senderDialogOpen} onClose={() => !saving && setSenderDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
                    Add sender email
                    <IconButton onClick={() => setSenderDialogOpen(false)}><Close /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Verified domain</InputLabel>
                                <Select label="Verified domain" value={senderForm.email_domain_id} onChange={(e) => setSenderForm((f) => ({ ...f, email_domain_id: e.target.value }))}>
                                    {verifiedDomains.map((d) => (
                                        <MenuItem key={d.id} value={String(d.id)}>{d.domain}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Local part (before @)" required value={senderForm.local_part} onChange={(e) => setSenderForm((f) => ({ ...f, local_part: e.target.value.replace(/@/g, "") }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Display name" value={senderForm.name} onChange={(e) => setSenderForm((f) => ({ ...f, name: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Reply-to (optional)" value={senderForm.reply_to} onChange={(e) => setSenderForm((f) => ({ ...f, reply_to: e.target.value }))} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setSenderDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateSender} disabled={saving}>Create sender</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dnsDialog.open} onClose={() => setDnsDialog({ open: false, domain: null, records: [], loading: false })} maxWidth="md" fullWidth>
                <DialogTitle>DNS records — {dnsDialog.domain?.domain}</DialogTitle>
                <DialogContent dividers>
                    {dnsDialog.loading ? <CircularProgress size={24} /> : (
                        <SesDnsRecordsTable records={dnsDialog.records} verificationStatus={dnsDialog.domain?.ses_verification_status} />
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setDnsDialog({ open: false, domain: null, records: [], loading: false })}>Close</Button></DialogActions>
            </Dialog>

            <Dialog open={deleteWorkerOpen} onClose={() => !saving && setDeleteWorkerOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <WarningAmber color="error" /> Delete email worker?
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                        Deletes the worker configuration only. Domains and SES identities are managed separately and are not removed by this action.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDeleteWorkerOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteWorker} disabled={saving}>Delete worker</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default AdminEmailServicePage;
